import React from 'react';
import './App.css';
import TitleScreen from './components/TitleScreen';
import PlayScreen from './components/PlayScreen';
import EndingScreen from './components/EndingScreen';
import LanguageSwitcher from './components/LanguageSwitcher';
import { I18nProvider } from './i18n';
import { CARDS } from './data/cards';
import { LOSS_ENDINGS, WIN_ENDINGS } from './data/endings';
import {
  STAT_KEYS, TOTAL_DAYS, SWIPE_THRESHOLD, SAVE_KEY,
} from './data/constants';

const ACCENT = '#6B53FD';
const AESTHETIC = 'flat';
const DAY_STYLE = 'ring';

function loadSave() {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return { highScore: 0, unlocked: [] };
    return { highScore: 0, unlocked: [], ...JSON.parse(raw) };
  } catch {
    return { highScore: 0, unlocked: [] };
  }
}
function saveSave(s) {
  try { localStorage.setItem(SAVE_KEY, JSON.stringify(s)); } catch {}
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
function buildDeck() {
  return shuffle(CARDS.filter((c) => !c.followup));
}

const FOLLOWUP_BY_ID = (() => {
  const m = {};
  CARDS.forEach((c) => { if (c.followup) m[c.id] = c; });
  return m;
})();

function clamp(n) { return Math.max(0, Math.min(100, n)); }

function applyDeltas(stats, choice) {
  const next = { ...stats };
  STAT_KEYS.forEach((k) => { next[k] = clamp(next[k] + (choice[k] || 0)); });
  return next;
}

function checkLoss(stats) {
  for (const k of STAT_KEYS) {
    if (stats[k] <= 0) return LOSS_ENDINGS[`${k}0`];
    if (stats[k] >= 100) return LOSS_ENDINGS[`${k}100`];
  }
  return null;
}

function chooseWinEnding(stats) {
  return WIN_ENDINGS.find((w) => w.test(stats)) || WIN_ENDINGS[WIN_ENDINGS.length - 1];
}

export default function App() {
  const [phase, setPhase] = React.useState('title');
  const [save, setSave] = React.useState(loadSave);
  const [stats, setStats] = React.useState({ e: 50, x: 50, u: 50, p: 50 });
  const [day, setDay] = React.useState(0);
  const [deck, setDeck] = React.useState(() => buildDeck());
  const [drag, setDrag] = React.useState({ x: 0, dragging: false });
  const [pulse, setPulse] = React.useState({});
  const [pendingFollowups, setPendingFollowups] = React.useState([]);
  const [exiting, setExiting] = React.useState(null);
  const [exitDetail, setExitDetail] = React.useState(null);
  const [ending, setEnding] = React.useState(null);
  const [isWin, setIsWin] = React.useState(false);

  const beginRun = React.useCallback(() => {
    setStats({ e: 50, x: 50, u: 50, p: 50 });
    setDay(0);
    setDeck(buildDeck());
    setPendingFollowups([]);
    setEnding(null);
    setIsWin(false);
    setDrag({ x: 0, dragging: false });
    setExiting(null);
    setExitDetail(null);
    setPhase('play');
  }, []);

  const currentCard = React.useMemo(() => {
    const due = pendingFollowups.find((f) => f.atDay <= day + 1);
    if (due && FOLLOWUP_BY_ID[due.id]) return FOLLOWUP_BY_ID[due.id];
    return deck[0];
  }, [deck, pendingFollowups, day]);

  const finalize = React.useCallback((endingResult, win, finalDay, finalStats) => {
    setEnding(endingResult);
    setIsWin(win);
    setStats(finalStats);
    setSave((prev) => {
      const next = {
        highScore: Math.max(prev.highScore, finalDay),
        unlocked: Array.from(new Set([...prev.unlocked, endingResult.id])),
      };
      saveSave(next);
      return next;
    });
    setTimeout(() => setPhase('end'), 300);
  }, []);

  const commitChoice = React.useCallback((side) => {
    if (!currentCard || exiting) return;
    const choice = side === 'left' ? currentCard.left : currentCard.right;
    const newStats = applyDeltas(stats, choice);

    const changed = {};
    STAT_KEYS.forEach((k) => { if (newStats[k] !== stats[k]) changed[k] = true; });
    setPulse(changed);
    setTimeout(() => setPulse({}), 600);

    let nextPending = pendingFollowups;
    if (choice.triggers && FOLLOWUP_BY_ID[choice.triggers]) {
      nextPending = [...pendingFollowups, { id: choice.triggers, atDay: day + 4 }];
    }

    let nextDeck = deck;
    let nextPending2 = nextPending;
    const wasFollowup = currentCard.followup;
    if (wasFollowup) {
      nextPending2 = nextPending.filter((f) => f.id !== currentCard.id);
    } else {
      nextDeck = deck.slice(1);
    }

    const nextDay = day + 1;
    setExitDetail({ card: currentCard, direction: side, fromX: drag.x, aesthetic: AESTHETIC });
    setStats(newStats);
    setDay(nextDay);
    setDeck(nextDeck);
    setPendingFollowups(nextPending2);
    setExiting(side);
    setDrag({ x: 0, dragging: false });

    setTimeout(() => {
      setExiting(null);
      setExitDetail(null);
      const loss = checkLoss(newStats);
      if (loss) {
        finalize(loss, false, nextDay, newStats);
      } else if (nextDay >= TOTAL_DAYS) {
        finalize(chooseWinEnding(newStats), true, nextDay, newStats);
      } else if (nextDeck.length === 0 && nextPending2.length === 0) {
        finalize(chooseWinEnding(newStats), true, nextDay, newStats);
      }
    }, 520);
  }, [currentCard, exiting, stats, deck, pendingFollowups, day, drag.x, finalize]);

  const startRef = React.useRef({ x: 0 });
  const onPointerDown = (e) => {
    if (exiting) return;
    e.preventDefault();
    e.target.setPointerCapture?.(e.pointerId);
    startRef.current = { x: e.clientX };
    setDrag({ x: 0, dragging: true });
  };
  React.useEffect(() => {
    if (!drag.dragging) return;
    const move = (ev) => {
      setDrag({ x: ev.clientX - startRef.current.x, dragging: true });
    };
    const up = (ev) => {
      const dx = ev.clientX - startRef.current.x;
      if (dx <= -SWIPE_THRESHOLD) commitChoice('left');
      else if (dx >= SWIPE_THRESHOLD) commitChoice('right');
      else setDrag({ x: 0, dragging: false });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [drag.dragging, commitChoice]);

  React.useEffect(() => {
    if (phase !== 'play') return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') commitChoice('left');
      else if (e.key === 'ArrowRight') commitChoice('right');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, commitChoice]);


  return (
    <I18nProvider>
      <div className="app" style={{ '--accent': ACCENT }}>
        <LanguageSwitcher phase={phase} />
        {phase === 'title' && (
          <TitleScreen onStart={beginRun} save={save} accent={ACCENT} />
        )}
        {phase === 'play' && (
          <PlayScreen
            stats={stats}
            accent={ACCENT}
            pulse={pulse}
            drag={drag}
            deck={deck}
            currentCard={currentCard}
            onPointerDown={onPointerDown}
            onChoice={commitChoice}
            exiting={exiting}
            exitDetail={exitDetail}
            aesthetic={AESTHETIC}
            day={day}
            dayStyle={DAY_STYLE}
          />
        )}
        {phase === 'end' && ending && (
          <EndingScreen
            ending={ending}
            day={day}
            stats={stats}
            save={save}
            isWin={isWin}
            onRestart={beginRun}
            accent={ACCENT}
          />
        )}
      </div>
    </I18nProvider>
  );
}
